// @ts-nocheck
import React, { useState, useEffect } from "react"
import { 
  Box, 
  Container, 
  Text, 
  Grid, 
  GridItem, 
  VStack,
  HStack,
  Flex,
  Button,
  Heading,
  Badge
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"

import useAuth from "@/hooks/useAuth"
import EquipmentDetailDialog from "@/components/Dashboard/EquipmentDetailDialog"
import PredictiveMaintenanceDashboard from "@/components/Dashboard/PredictiveMaintenanceDashboard"
import ChatBot from "@/components/Dashboard/Chatbot"

export const Route = createFileRoute("/_layout/ai")({
  component: Dashboard,
})

function Dashboard() {
  const { user: currentUser } = useAuth()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedEquipment, setSelectedEquipment] = useState(null)
  const [activeView, setActiveView] = useState('monitoring') // 'monitoring' 또는 'predictive'
  
  // 샘플 데이터 상태
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalEquipment: 18,
      onlineEquipment: 16,
      alerts: 2,
      productivity: 92.3
    },
    equipmentStatus: [
      { id: 1, name: 'CNC 밀링머신 #1', status: '온라인', temperature: 48.23, vibration: 2.15, efficiency: 94.67, spindle: 8500.12, feed: 1200.45 },
      { id: 2, name: 'CNC 선반 #3', status: '온라인', temperature: 44.89, vibration: 1.82, efficiency: 91.23, spindle: 3200.67, feed: 850.90 },
      { id: 3, name: '5축 머시닝센터 #2', status: '경고', temperature: 73.45, vibration: 4.23, efficiency: 68.12, spindle: 12000.34, feed: 2500.78 },
      { id: 4, name: 'CNC 보링머신 #1', status: '오프라인', temperature: 23.12, vibration: 0, efficiency: 0, spindle: 0, feed: 0 },
      { id: 5, name: 'CNC 선반 #1', status: '온라인', temperature: 42.67, vibration: 1.64, efficiency: 88.45, spindle: 2800.23, feed: 950.12 },
      { id: 6, name: 'CNC 밀링머신 #4', status: '온라인', temperature: 46.34, vibration: 1.91, efficiency: 96.78, spindle: 9200.56, feed: 1450.89 },
      { id: 7, name: 'CNC 드릴링머신 #2', status: '온라인', temperature: 38.91, vibration: 2.84, efficiency: 82.56, spindle: 4500.78, feed: 380.45 },
      { id: 8, name: 'CNC 그라인더 #1', status: '경고', temperature: 78.23, vibration: 4.87, efficiency: 65.34, spindle: 18000.12, feed: 120.67 },
      { id: 9, name: '수직 머시닝센터 #5', status: '온라인', temperature: 51.56, vibration: 2.34, efficiency: 89.12, spindle: 10500.89, feed: 1800.23 }
    ],
    alerts: [
      { id: 1, type: 'warning', message: '5축 머시닝센터 #2 스핀들 온도 상승', time: '3분 전', severity: 'high' },
      { id: 2, type: 'error', message: 'CNC 보링머신 #1 비상정지 발생', time: '12분 전', severity: 'critical' },
      { id: 3, type: 'warning', message: 'CNC 그라인더 #1 진동 수치 임계값 초과', time: '8분 전', severity: 'high' },
      { id: 4, type: 'info', message: 'CNC 밀링머신 #1 정기 점검 완료', time: '25분 전', severity: 'low' }
    ],
    productionData: [
      { time: '06:00', production: 145, target: 160 },
      { time: '08:00', production: 168, target: 160 },
      { time: '10:00', production: 175, target: 160 },
      { time: '12:00', production: 152, target: 160 },
      { time: '14:00', production: 188, target: 160 },
      { time: '16:00', production: 171, target: 160 }
    ]
  })

  // AI 알림 데이터
  const [aiAlerts, setAiAlerts] = useState([
    {
      id: 1,
      equipmentId: 3,
      type: 'prediction',
      severity: 'high',
      message: '5축 머시닝센터 #2, 72시간 내 냉각시스템 고장 예측 (확률: 78%)',
      aiConfidence: 78,
      recommendedAction: '냉각수 교체 및 펌프 점검',
      estimatedCost: '150만원',
      time: '5분 전'
    },
    {
      id: 2,
      equipmentId: 8,
      type: 'anomaly',
      severity: 'medium',
      message: 'CNC 그라인더 #1, 비정상 진동 패턴 감지',
      aiConfidence: 65,
      recommendedAction: '베어링 상태 점검',
      estimatedCost: '80만원',
      time: '12분 전'
    },
    {
      id: 3,
      equipmentId: 1,
      type: 'optimization',
      severity: 'low',
      message: 'CNC 밀링머신 #1, 스핀들 속도 최적화로 15% 효율 향상 가능',
      aiConfidence: 82,
      recommendedAction: '가공 파라미터 조정',
      estimatedCost: '무료',
      time: '25분 전'
    }
  ])

  // 실시간 업데이트 시뮬레이션
  useEffect(() => {
    const interval = setInterval(() => {
      setDashboardData(prev => ({
        ...prev,
        equipmentStatus: prev.equipmentStatus.map(equipment => {
          if (equipment.status !== '온라인') return equipment
          
          return {
            ...equipment,
            temperature: Math.round(Math.max(30, Math.min(80, 
              equipment.temperature + (Math.random() - 0.5) * 1)) * 100) / 100,
            vibration: Math.round(Math.max(0, Math.min(5, 
              equipment.vibration + (Math.random() - 0.5) * 0.1)) * 100) / 100,
            efficiency: Math.round(Math.max(0, Math.min(100, 
              equipment.efficiency + (Math.random() - 0.5) * 2)) * 100) / 100,
            spindle: Math.round(Math.max(0, Math.min(25000, 
              equipment.spindle + (Math.random() - 0.5) * 200)) * 100) / 100,
            feed: Math.round(Math.max(0, Math.min(3000, 
              equipment.feed + (Math.random() - 0.5) * 50)) * 100) / 100
          }
        })
      }))
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  const getStatusColor = (status) => {
    switch(status) {
      case '온라인': return '#28a745'
      case '경고': return '#ffc107'
      case '오프라인': return '#dc3545'
      default: return '#6c757d'
    }
  }

  const getAlertIcon = (severity) => {
    switch(severity) {
      case 'critical': return '🚨'
      case 'high': return '⚠️'
      case 'low': return 'ℹ️'
      default: return 'ℹ️'
    }
  }

  const handleEquipmentClick = (equipment) => {
    setSelectedEquipment(equipment)
    setIsDialogOpen(true)
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setSelectedEquipment(null)
  }

  const CustomCard = ({ children, ...props }) => (
    <Box 
      bg="white" 
      border="1px" 
      borderColor="gray.200" 
      borderRadius="md" 
      p={4} 
      shadow="md"
      {...props}
    >
      {children}
    </Box>
  )

  const StatCard = ({ icon, label, value, helpText, valueColor = "#3182ce" }) => (
    <Box textAlign="center">
      <Text fontSize="sm" color="gray.600" mb={1}>
        {icon} {label}
      </Text>
      <Text fontSize="3xl" fontWeight="bold" color={valueColor} mb={1}>
        {value}
      </Text>
      <Text fontSize="xs" color="gray.500">
        {helpText}
      </Text>
    </Box>
  )

  const SimpleProgress = ({ value, max = 100, color = "#3182ce", height = "8px" }) => (
    <Box w="100%" bg="gray.200" borderRadius="md" overflow="hidden" h={height}>
      <Box 
        h="100%" 
        bg={color} 
        borderRadius="md"
        w={`${Math.min((value / max) * 100, 100)}%`}
        transition="width 0.3s ease"
      />
    </Box>
  )

  // AI 알림 카드
  const AIAlertsCard = () => (
    <CustomCard>
      <Heading size="md" color="purple.600" mb={4}>🤖 AI 예지보전 알림</Heading>
      <VStack gap={4} align="stretch">
        {aiAlerts.map(alert => (
          <Box 
            key={alert.id}
            p={3}
            bg="purple.50"
            borderRadius="md"
            borderLeft="4px"
            borderLeftColor={
              alert.severity === 'high' ? 'red.500' :
              alert.severity === 'medium' ? 'orange.500' : 'blue.500'
            }
          >
            <VStack align="stretch" gap={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium" color="purple.800">
                  {alert.message}
                </Text>
                <Badge colorScheme="purple" size="sm">
                  AI {alert.aiConfidence}%
                </Badge>
              </HStack>
              
              <Text fontSize="xs" color="purple.600">
                📋 권장: {alert.recommendedAction}
              </Text>
              
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.500">{alert.time}</Text>
                <Text fontSize="xs" color="purple.500">
                  예상비용: {alert.estimatedCost}
                </Text>
              </HStack>
            </VStack>
          </Box>
        ))}
      </VStack>
    </CustomCard>
  )


  return (
    <Box minH="100%" w="100%" overflow="hidden">
      <Box 
        h="100%" 
        w="100%"
        overflowY="auto" 
        pt={4} 
        px={6} 
        pb={4}
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {/* 헤더 */}
        <Box 
          // position="sticky" 
          top={0} 
          bg="white" 
          zIndex={10} 
          py={4} 
          mb={4}
          borderBottom="1px"
          borderColor="gray.200"
          mx={-6}
          px={6}
        >
          <Flex justify="space-between" align="center" mb={0}>
            <Heading size="3xl">
              AI 예지보전
            </Heading>
            <VStack align="end" gap={1}>
              <Text fontSize="sm" color="gray.500">
                마지막 업데이트: {new Date().toLocaleTimeString()}
              </Text>
              <Button size="sm" colorScheme="blue" onClick={() => window.location.reload()}>
                🔄 새로고침
              </Button>
            </VStack>
          </Flex>
        </Box>

        
        <PredictiveMaintenanceDashboard equipmentStatus={dashboardData.equipmentStatus} />
        
      </Box>

      {/* 장비 상세 다이얼로그 */}
      <EquipmentDetailDialog 
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        equipment={selectedEquipment}
      />

      {dashboardData ? <ChatBot dashboardData={dashboardData} /> : <Spinner />}
    </Box>
  )
}

export default Dashboard